import { SearchFormAutocompleteProvider } from '@pcln/horizon-search-form'
import type {
  AutocompleteSearchFieldItem,
  AutocompleteSearchFieldLocation,
  SearchFormAutocompleteConfig,
} from '@pcln/horizon-search-form'
import CONFIG from 'isomorphic-config'
import { useCallback, useMemo, type ReactNode } from 'react'

import { getHotelTypeaheadFirstLineSuffix } from '@/components/AutocompleteWrapper/getHotelTypeaheadFirstLineSuffix'
import fireTypeaheadClickEvent, { fireAutocompleteOpenEvent } from '@/components/TypeAhead/ga4'
import { useAbandonedSearchesFetched } from '@/hooks/useAbandonedSearchesFetched'
import useBootstrapData from '@/hooks/useBootstrapData'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useRecentSearchesFetched from '@/hooks/useRecentSearchesFetched'
import useRecentSearchesLocalStorage from '@/hooks/useRecentSearchesLocalStorage'
import useRecommendations from '@/hooks/useRecommendations'
import useRecommendedCityIds from '@/hooks/useRecommendedCityIds'
import useSeti from '@/hooks/useSeti'
import { useViewedSearchesFetched } from '@/hooks/useViewedSearchesFetched'
import analytics from '@/shared-utils/analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import { recentSearchesKey } from '../AutocompleteWrapper/constants'
import { top20Filters } from '../TypeAhead/constants'
import {
  HTL_TYPEAHEAD_POPULAR_TRENDING_EXPERIMENT,
  isHotelTypeaheadEndpoint,
} from '../TypeAhead/constants/popularTrending'
import { fireFilterBarChipGA4Event } from '../TypeAhead/FilterBar/ga4'
import logLtrModelVariantSearch from '../TypeAhead/log-ltr-model-variant-search'
import type { TypeAheadConfigEndpoint } from '../TypeAhead/types'
import { usePopularTrendingImpression } from '../TypeAhead/usePopularTrendingImpression'
import { cityIdExistsInTop20Filters } from '../TypeAhead/utils'
import { resolveTypeaheadItemName } from '../TypeAhead/utils/resolveTypeaheadItemName'

const AI_SEARCH_BUTTON_LINK_NAME = {
  VARIANT1: 'ask_ai_mode_typeahead',
  VARIANT2: 'ai_mode_typeahead',
} as const

/**
 * `LOCATION_SEARCH_TYPE` and `AutocompleteSearchFieldLocation` describe the same runtime shape
 * (next-landing's `type`/`subType` unions add `'AI_QUERY'`, which the shared package's don't
 * enumerate) — this boundary cast is the intentional reconciliation point, not a real type gap.
 */
function toSearchFieldLocations(items: LOCATION_SEARCH_TYPE[]): AutocompleteSearchFieldLocation[] {
  return items as unknown as AutocompleteSearchFieldLocation[]
}

/**
 * Supplies next-landing's session, endpoint config, search data and analytics to the location
 * autocomplete that `@pcln/horizon-search-form`'s search forms render internally.
 *
 * Must sit inside a `QueryClientProvider` — recents and recommendations are react-query hooks.
 */
export function SearchFormAutocompleteWiring({ children }: Readonly<{ children: ReactNode }>) {
  const {
    webstats: { clientGUID, requestId },
    appName,
    appVersion,
    signInStatus: { signedIn: serverSignedIn },
  } = useBootstrapData()
  const { signedIn: clientSideSignedIn } = useClientSideSignedInData()
  const signedIn = serverSignedIn || clientSideSignedIn

  const popularTrendingVariant = useSeti({
    experimentCode: HTL_TYPEAHEAD_POPULAR_TRENDING_EXPERIMENT,
    shouldFireImpression: false,
  })

  const mergeAbandonedViewedAndRecentSearches =
    useSeti({
      experimentCode: 'DESK_MWEB_HTL_RECENT_SEARCH_LOGIC_UPDATE',
      shouldFireImpression: false,
    }) === 'VARIANT'

  const [recentSearchesFetched, areRecentSearchesReady] = useRecentSearchesFetched({
    cguid: clientGUID,
    appName,
    appVersion,
    signedIn,
    sortByCreationDateTime: mergeAbandonedViewedAndRecentSearches,
  })

  const [abandonedSearchesFetched, areAbandonedSearchesReady] = useAbandonedSearchesFetched({
    cguid: clientGUID,
    appName,
    appVersion,
    signedIn,
    enabled: mergeAbandonedViewedAndRecentSearches,
  })
  const [viewedSearchesFetched, areViewedSearchesReady] = useViewedSearchesFetched({
    cguid: clientGUID,
    appName,
    appVersion,
    signedIn,
    enabled: mergeAbandonedViewedAndRecentSearches,
  })

  // Persisted recents live under one localForage key per product, and each key holds both the
  // origin and destination side — so read every key here and let the field select its slice.
  const [hotelStart, hotelEnd] = useRecentSearchesLocalStorage(recentSearchesKey.hotels)
  const [airStart, airEnd] = useRecentSearchesLocalStorage(recentSearchesKey.flights)
  const [carStart, carEnd] = useRecentSearchesLocalStorage(recentSearchesKey.cars)
  const [pkgStart, pkgEnd] = useRecentSearchesLocalStorage(recentSearchesKey.packagesOrigin)

  const { recommendedCityIds } = useRecommendedCityIds({
    shouldFetchRecommendedCityIds: signedIn,
    typeAheadEndpoint: 'hotels',
  })
  const { data: recommendedSearches } = useRecommendations(recommendedCityIds)

  const getPersistedRecents = useCallback(
    ({ searchKey, typeAheadEndpoint }: { searchKey: string; typeAheadEndpoint: string }) => {
      const key = recentSearchesKey[typeAheadEndpoint as keyof typeof recentSearchesKey]
      const isStart = searchKey.includes('startLocation')
      if (key === recentSearchesKey.flights) return toSearchFieldLocations(isStart ? airStart : airEnd)
      if (key === recentSearchesKey.cars) return toSearchFieldLocations(isStart ? carStart : carEnd)
      if (key === recentSearchesKey.packagesOrigin) return toSearchFieldLocations(isStart ? pkgStart : pkgEnd)
      return toSearchFieldLocations(isStart ? hotelStart : hotelEnd)
    },
    [hotelStart, hotelEnd, airStart, airEnd, carStart, carEnd, pkgStart, pkgEnd],
  )

  const firePopularTrendingImpression = usePopularTrendingImpression(true)

  const config = useMemo<SearchFormAutocompleteConfig>(
    () => ({
      session: { clientGUID, appName, appVersion, signedIn, requestId },
      // Deployed environments override these paths with absolute URLs via env vars, so the app's
      // resolved config always wins over the package defaults.
      typeahead: {
        endpoint: CONFIG.client.typeahead.location.endpoint,
        suffix: CONFIG.client.typeahead.location.suffix,
        querystring: CONFIG.client.typeahead.location.querystring,
        feedback: CONFIG.client.typeahead.feedback,
      },
      geolocation: { nearestCityBaseUrl: CONFIG.client.search.retail.hotels },
      recentSearches: {
        getPersisted: getPersistedRecents,
        fetched: toSearchFieldLocations(recentSearchesFetched),
        isFetchedReady: areRecentSearchesReady,
        abandoned: toSearchFieldLocations(abandonedSearchesFetched),
        isAbandonedReady: areAbandonedSearchesReady,
        viewed: toSearchFieldLocations(viewedSearchesFetched),
        isViewedReady: areViewedSearchesReady,
      },
      recommendedSearches: { items: toSearchFieldLocations(recommendedSearches) },
      analytics: {
        onTypeaheadClick: ({ item, listType, typeAheadEndpoint }) => {
          const location = item as unknown as LOCATION_SEARCH_TYPE
          const endpoint = typeAheadEndpoint as TypeAheadConfigEndpoint
          const searchType = location.type === 'AI_QUERY' ? 'typein_ai_query' : listType
          const hotelItemName = resolveTypeaheadItemName({
            item: location,
            isHotelTypeahead: isHotelTypeaheadEndpoint(endpoint),
            popularTrendingVariant,
            searchType,
          })
          fireTypeaheadClickEvent(endpoint, hotelItemName ?? searchType)
          return hotelItemName ?? searchType
        },
        onAutocompleteOpen: ({ typeAheadEndpoint, listType }) => {
          const endpoint = typeAheadEndpoint as TypeAheadConfigEndpoint
          fireAutocompleteOpenEvent(endpoint, listType)
          // Hotel-only experiment: opening the flights/cars/packages typeahead must not record an
          // impression for it.
          if (isHotelTypeaheadEndpoint(endpoint)) {
            firePopularTrendingImpression()
          }
        },
        onFilterChipToggle: (item, selected) => {
          fireFilterBarChipGA4Event(item.filterType, item.name, selected)
        },
        onAiSearchClick: ({ query, variant }) => {
          analytics.fireGA4Event({
            event: 'internal_link',
            attributes: {
              type: 'ai_mode_penny_search',
              link_name: AI_SEARCH_BUTTON_LINK_NAME[variant],
              page_name: 'homepage',
              product_name: 'penny',
            },
          })
          const prompt = query ? encodeURIComponent(`Hotels near ${query}`) : ''
          window.open(`/penny${prompt ? `?askPrompt=${prompt}` : ''}`, '_blank', 'noopener,noreferrer')
        },
        onLtrSearch: ({ searchQuery, searchQueryLength, rankBy }) => {
          logLtrModelVariantSearch({ searchQuery, searchQueryLength, rankBy, requestId })
        },
        onError: (error) => {
          analytics.logError({ message: 'Search form autocomplete error', error: JSON.stringify(error) })
        },
      },
      // Popular/Trending badges are hotel-only — a non-hotel city row must not render one.
      resolveItemFirstLineSuffix: (item: AutocompleteSearchFieldItem, { typeAheadEndpoint }) =>
        getHotelTypeaheadFirstLineSuffix(item, {
          isHotelTypeahead: isHotelTypeaheadEndpoint(typeAheadEndpoint as TypeAheadConfigEndpoint),
          popularTrendingVariant,
        }),
      resolveFilterChipData: (currentCityId) =>
        cityIdExistsInTop20Filters(currentCityId) ? top20Filters[currentCityId] : undefined,
    }),
    [
      clientGUID,
      appName,
      appVersion,
      signedIn,
      requestId,
      recentSearchesFetched,
      areRecentSearchesReady,
      abandonedSearchesFetched,
      areAbandonedSearchesReady,
      viewedSearchesFetched,
      areViewedSearchesReady,
      getPersistedRecents,
      recommendedSearches,
      popularTrendingVariant,
      firePopularTrendingImpression,
    ],
  )

  return <SearchFormAutocompleteProvider config={config}>{children}</SearchFormAutocompleteProvider>
}
